import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { PaymentRequestButtonElement } from "@stripe/react-stripe-js";

import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";

import useApplePay from "../../hooks/use-apple-pay";

import { PaymentSystem } from "../../constants";
import { getPaymentSystem } from "../../utils/storage";

import classes from "./style.module.css";

/**
 * Google Pay for Stripe.
 *
 * Stripe's `PaymentRequest` — the very object behind the Apple Pay button —
 * resolves to Google Pay on Chrome/Android, so this reuses `useApplePay` and
 * differs only in which wallet it renders for and in the `method` it reports.
 * The payload it produces is the same `payment_method_id` a card produces, so
 * the purchase pipeline (intro one-time payment + subscription) and the backend
 * see nothing new.
 *
 * Stripe-only on purpose: Solidgate draws its own Google Pay button into the
 * container the payment modal provides for SDK-rendered wallet buttons.
 *
 * The button is rendered by Stripe (`PaymentRequestButtonElement`) rather than
 * by us — Google's brand guidelines require their own button art, and Stripe's
 * element already ships it.
 */
export const GooglePayButton = ({
  product,
  label,
  disabled,
  className,
  onClick,
  onSubmit,
  email,
  onRequireEmail,
  onAvailabilityChange,
}) => {
  const paymentSystem = getPaymentSystem();
  const {
    collectEmailAfterPayment,
    requestWalletEmail,
    requestApplePayEmail,
  } = useRemoteConfig();

  // `requestApplePayEmail` is the pre-rename key still configured on most
  // funnels; `requestWalletEmail` is `null` until one is migrated.
  const requestEmail = requestWalletEmail ?? requestApplePayEmail;

  const isStripe = paymentSystem === PaymentSystem.STRIPE;

  // NOTE: `collectApplePayBillingData` is deliberately not honoured here. It
  // works by requesting the payer's name — the only lever Stripe gives for the
  // billing address — and Google Pay turns any requested contact field into a
  // required "Contact info" row, greying out its Continue button until the user
  // fills it in by hand. The address is not worth a wallet sheet that cannot be
  // submitted; Apple Pay, whose sheet fills it silently, still collects it.

  // Deferred-email flow: an anonymous user (with a billing token) already
  // exists, so the charge needs no email — it is collected afterwards on the
  // email-after-payment page. Don't block the native sheet on a missing email.
  const emailOptional = Boolean(collectEmailAfterPayment) && isStripe;

  const { id, amount, isIntroductory, trial_price_amount, currency } = product;

  const options = useMemo(
    () => {
      const totalAmount = !isIntroductory ? amount : trial_price_amount;
      const opts = {
        label,
        amount: totalAmount,
        currency,
        // The deferred-email flow is the only reason to ask the sheet for
        // anything: there the email is not known yet and asking here saves the
        // user a whole page. Nothing else is requested — see the note above.
        requestPayerEmail: Boolean(requestEmail) && emailOptional,
      };

      // Intro/trial flow: surface the price breakdown in the sheet —
      // line 1 = full plan price, line 2 = discount, total = today's charge.
      // Skip if the math doesn't add up.
      const fullAmount = Number(amount);
      const introAmountNum = Number(trial_price_amount);
      if (
        isIntroductory
        && fullAmount > 0
        && introAmountNum >= 0
        && fullAmount > introAmountNum
      ) {
        opts.displayItems = [
          { label: label || 'Plan', amount: fullAmount },
          { label: 'Discount', amount: -(fullAmount - introAmountNum) },
        ];
      }

      return opts;
    },
    [amount, isIntroductory, label, trial_price_amount, currency, emailOptional, requestEmail]
  );

  const { canMakePayment, paymentRequest } = useApplePay(options, {
    onMethod: (res) => {
      const { complete, paymentMethod } = res;
      const { trialDays, onetimePriceId } = product;

      const formData = {
        price_id: id,
        method: "googlepay",
        currency,
        email,
        payment_method_id: paymentMethod?.id,
        trial_period_days: trialDays,
      };

      if (isIntroductory && onetimePriceId) {
        formData.introductory_price_id = onetimePriceId;
      }

      // The paywall reads this one stash for every wallet (Apple Pay, and the
      // TailoredPay wallet handler) to decide whether it can skip the
      // email-after-payment page — hence the Apple-flavoured name. Set on every
      // authorization, including to undefined, so nothing stale is inherited.
      if (emailOptional && typeof window !== "undefined") {
        window.__apple_pay_payer_email__ = res.payerEmail || undefined;
      }

      complete("success");

      onSubmit(formData);
    },
  });

  // `canMakePayment` tells Apple Pay and Google Pay apart: on iOS/macOS Safari
  // the same PaymentRequest resolves to Apple Pay, and that sheet belongs to
  // ApplePayButton. It stays false on plain HTTP too — Stripe refuses to set up
  // Payment Request there at all.
  const isAvailable = Boolean(isStripe && paymentRequest && canMakePayment?.googlePay);

  // Told to the modal so it doesn't reserve a slot (and draw an "or") for a
  // button that never shows up.
  useEffect(() => {
    onAvailabilityChange?.(isAvailable);
  }, [isAvailable, onAvailabilityChange]);

  if (!isAvailable) {
    return null;
  }

  const isBlocked = disabled || (!email && !emailOptional);

  const handleClick = (event) => {
    if (onClick) {
      onClick();
    }

    // The element opens the sheet on its own — cancelling the click is the only
    // way to hold it back.
    if (isBlocked) {
      event?.preventDefault();
      if (!disabled) {
        onRequireEmail?.();
      }
    }
  };

  // Clicked programmatically by the on-page injected Google Pay tile, which
  // looks up whatever carries this label (Solidgate renders such a button
  // itself). Stripe's button lives in an iframe and cannot be reached from
  // here, so this proxy is what that lookup finds — and `show()` still counts
  // as user-initiated, since it runs inside the original click.
  const handleProxyClick = () => {
    if (onClick) {
      onClick();
    }

    if (isBlocked) {
      if (!disabled) {
        onRequireEmail?.();
      }
      return;
    }

    paymentRequest.show();
  };

  return (
    <>
      <PaymentRequestButtonElement
        // Stripe refuses to swap `paymentRequest` on a mounted element, and a
        // new one is built whenever the plan (and with it the total) changes —
        // so remount instead of showing a stale amount in the sheet.
        key={`${id}-${!isIntroductory ? amount : trial_price_amount}-${currency}`}
        className={classNames(classes["google-pay-button"], className)}
        onClick={handleClick}
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: "default",
              theme: "dark",
              height: "50px",
            },
          },
        }}
      />
      <button
        type="button"
        aria-label="Google Pay"
        className={classes["google-pay-proxy"]}
        onClick={handleProxyClick}
      />
    </>
  );
};

GooglePayButton.propTypes = {
  product: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.any,
  onClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  email: PropTypes.string,
  onRequireEmail: PropTypes.func,
  onAvailabilityChange: PropTypes.func,
};

GooglePayButton.defaultProps = {
  disabled: false,
  className: null,
  onClick: null,
  email: null,
  onRequireEmail: null,
  onAvailabilityChange: null,
};
